import { Suspense, useCallback, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { getURLPrefix } from "../../utils/getUrlPrefix";
import { PRODUCTION_GV_API_AUTH_CLIENT_ID, PRODUCTION_GV_API_AUTH_CLIENT_SECRET } from "../../utils/gvSecrets";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import DownloadPDF from "../../components/custom/DownloadPDF";
import Loading from "../../components/core/blocks/Loading";

import { Col, Row } from "react-bootstrap";
import { Button } from "../../components/core/basic/Button";
import { Flex } from "../../../styled-system/jsx";
import { Modal } from "../../components/core/basic/Modal";
import { tokenLogin } from "../../modules/auth/tokens/tokenLogin";
import { ReportLoadedContext } from "./-reportLoadedContext";

const PDFView = () => {
  const { t } = useTranslation(["common"]);
  const dispatch = useAppDispatch();
  const search = useLocation({ select: (location) => location.searchStr });

  const [loaded, setLoaded] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(true);
  const [instructionsViewed, setInstructionsViewed] = useState(false);

  const params = new URLSearchParams(search);
  const token = params.get("token");
  const print = params.get("print");

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const onReportLoaded = useCallback(() => setLoaded(true), []);
  const user_full_name = useAppSelector((state) => state.user.user_full_name);

  const loginMutation = useMutation({
    mutationFn: async (token) => {
      const authData = {
        client_id: PRODUCTION_GV_API_AUTH_CLIENT_ID,
        client_secret: PRODUCTION_GV_API_AUTH_CLIENT_SECRET,
        grant_type: "password",
        password: "password",
        username: "username",
        token,
      };
      const urlPrefix = getURLPrefix();
      const resp = await fetch(`${urlPrefix}/api/auth_otp/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(authData),
      });
      if (!resp.ok) {
        throw new Error("Invalid Token");
      }
      const oAuthTokens = await resp.json();
      await tokenLogin(oAuthTokens, dispatch);
      setIsLoggedIn(true);
    },
  });

  useEffect(() => {
    if (token) {
      loginMutation.mutate(token);
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (print === "true" && loaded && instructionsViewed) {
      // Hide Hubspot chat and react-query-devtools widgets in print view
      const reactQueryDevtoolsWidget = document.querySelector(".ReactQueryDevtools");
      const hubspotChatWidget = document.querySelector("#hubspot-messages-iframe-container");

      if (hubspotChatWidget) {
        hubspotChatWidget.style.visibility = "hidden";
      }

      if (reactQueryDevtoolsWidget) {
        reactQueryDevtoolsWidget.style.display = "none";
      }

      window.print();
    }
  }, [print, loaded, instructionsViewed]);

  return (
    <div>
      {showInstructionsModal && !instructionsViewed && loaded && (
        <Modal
          isOpen={showInstructionsModal}
          onOpenChange={(isOpen) => {
            setShowInstructionsModal(isOpen);
            if (!isOpen) {
              setInstructionsViewed(true);
            }
          }}
          title={t(($) => $.common.pdfView["Printing Recommendations"])}
          css={{ width: "60vw" }}
        >
          <Row className="justify-content-center">
            <Col xs={12} xl={8}>
              <p>
                {t(
                  ($) =>
                    $.common.pdfView[
                      "For the best formatting of this report, please ensure the following printing settings are configured on your computer:"
                    ],
                )}{" "}
              </p>
              <ul>
                <li>{t(($) => $.common.pdfView["The page orientation is set to landscape"])}</li>
                <li>{t(($) => $.common.pdfView['Page margins are set to "None"'])}</li>
                <li>
                  {t(
                    ($) => $.common.pdfView['The "Headers and footers" checkbox under printing "Options" is unchecked'],
                  )}
                </li>
                <li>
                  {t(
                    ($) =>
                      $.common.pdfView[
                        'The "Background graphics" checkbox under printing "Options" is checked (optional, but strongly recommended)'
                      ],
                  )}
                </li>
              </ul>
            </Col>
            <Flex justifyContent={"center"}>
              <Button
                onPress={() => {
                  setShowInstructionsModal(false);
                  setInstructionsViewed(true);
                }}
              >
                {t(($) => $.common.pdfView["Print PDF"])}
              </Button>
            </Flex>
          </Row>
        </Modal>
      )}
      {!print && (
        <>
          <h1>{t(($) => $.common.pdfView["PDF View"])}</h1>
          {!token && <p>{t(($) => $.common.pdfView["No token found"])}</p>}
          {isLoggedIn && <p>{t(($) => $.common.pdfView["Logged in"])}</p>}
          {user_full_name && (
            <p>{t(($) => $.common.pdfView["Welcome {userFullName}"], { userFullName: user_full_name })}</p>
          )}
          {!token && !print && <DownloadPDF url="/pdf?print=true" />}
        </>
      )}
      {
        <Suspense fallback={<Loading fullPage={false} height={300} />}>
          <ReportLoadedContext.Provider value={onReportLoaded}>
            <Outlet />
          </ReportLoadedContext.Provider>
        </Suspense>
      }
    </div>
  );
};

export const Route = createFileRoute("/pdf")({
  component: PDFView,
});
